/**
 * Optimized function to sort shifts by start time
 * @param {Array} shifts - Array of shift objects
 * @returns {Array} - Sorted array of shifts
 */
export const sortShiftsByStartTime = (shifts) => {
  if (!Array.isArray(shifts) || shifts.length === 0) {
    return [];
  }

  // Create a Map for O(1) lookup of parsed times
  const timeCache = new Map();

  const parseTimeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== "string") return 0;

    // Check cache first
    if (timeCache.has(timeString)) {
      return timeCache.get(timeString);
    }

    const timeParts = timeString.split(":");
    if (timeParts.length !== 2) return 0;

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    if (isNaN(hours) || isNaN(minutes)) return 0;

    const totalMinutes = hours * 60 + minutes;
    timeCache.set(timeString, totalMinutes);

    return totalMinutes;
  };

  const getShiftStartTime = (shift) => {
    if (!shift || typeof shift !== "object") return 0;

    // Priority 1: Use shift_start from info object
    const startTime = shift?.info?.shift_start;
    if (startTime) {
      return parseTimeToMinutes(startTime);
    }

    // Priority 2: Parse from label (fallback)
    const label = shift?.label;
    if (label && typeof label === "string") {
      const timeMatch = label.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        if (!isNaN(hours) && !isNaN(minutes)) {
          return hours * 60 + minutes;
        }
      }
    }

    return 0;
  };

  // Sort with optimized comparison
  return shifts
    .filter((shift) => shift && typeof shift === "object")
    .sort((a, b) => {
      const timeA = getShiftStartTime(a);
      const timeB = getShiftStartTime(b);
      return timeA - timeB;
    });
};
